import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory, RouteComponentProps } from 'react-router-dom';
import { Skeleton } from '@material-ui/lab';
import { Card, Tab, Tabs } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import { IAppState } from '../../../store/rootDuck';
import { actions as mapActions } from '../../../store/ducks/yaMap.duck';
import { actions as orderActions } from '../../../store/ducks/orders.duck';
import homeStyles from '../../../constants/homeStyles';
import { setLayoutSubheader } from '../../../utils/layout';
import { getOrderTypeWithTranslates } from './constants';
import AlertDialog from '../../../components/other/Dialog/AlertDialog';
import { TabPanel } from '../../../components/other/Tab/TabPanel';
import { ILocation } from '../../../interfaces/locations';
import { useDefineUserRole } from '../../../hooks';
import { OrderForm } from './components/OrderForm';
import OrderTable from './components/OrderTable';
import { getInitialValuesOrderPage, useFormikOrderPage } from './hooks/useFormikOrderPage';
import { useGetStores } from '../cart/hooks/useGetStores';

export enum ViewMode {
  VIEW = 'view',
  EDIT = 'edit',
}

const OrderPage: React.FC<
  TPropsFromRedux & RouteComponentProps<{ id: string; mode: string }>
> = ({
  match: {
    params: { id, mode },
  },
  order,
  loading,
  fetchById,
  editError,
  editSuccess,
  clear,
  editStatus,
  fetchCDEK,
  fetchCDEKCities,
  cdekCities,
}) => {
  const [isAlertOpen, setAlertOpen] = useState(false);
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const homeClasses = homeStyles();
    const { fetchStores, stores, loading: loadingStores } = useGetStores();
  const [valueTabs, setValueTabs] = useState(0);

  const [chosenCdekCity, setChosenCdekCity] = useState<any>(null);
  const [cdekCitiesToShow, setCdekCitiesToShow] = useState<any>([]);
  const [shippingLocation, setShippingLocation] = useState<ILocation | null>(null);
  const [selectedId, setSelectedId] = useState(-1);
  const { me } = useSelector(({ profile }: any) => profile);
  const handleTabsChange = (event: any, newValue: number) => {
    setValueTabs(newValue);
  };
  const disabled = mode === ViewMode.VIEW;
  const isRoleAdmin = useDefineUserRole(me, 'ROLE_ADMIN');

  useEffect(() => {
    if (id) {
      fetchById(+id);
    }
  }, [id]);

  useEffect(() => {
    if (order?.delivery_type === 'self' && order?.point){
      setChosenCdekCity(order?.point.city_name);
    }
  },[order])

  useEffect(() => {
    setShippingLocation(localLocation);
    fetchCDEKCities();

    return () => {
      clear();
    };
  }, []);

  useEffect(() => {
    if (order?.store?.company){
      fetchStores(1, 100, order?.store?.company.id)
    }
  },[order?.store])

  const itemsIds: number[] = [];
  const ordersIds: number[] = [];

  order?.items?.forEach(item => {
    itemsIds.push(item.id);
  });
  order?.orders?.forEach(item => {
    ordersIds.push(item.id);
  });

  useEffect(() => {
    setCdekCitiesToShow(
      cdekCities
        ?.filter((i: any) => i.country_code === 'RU')
        .map((x: any) => ({ ...x, label: x.city, key: x.uuid })) || []
    );
  }, [cdekCities]);

  useEffect(() => {
    if (editSuccess || editError) {
      enqueueSnackbar(
        editSuccess
          ? ` ${intl.formatMessage({
              id: 'ORDER.SNACKBAR.EDIT',
            })}`
          : `${intl.formatMessage({ id: 'PRODUCT.SNACKBAR.ERROR' })} ${editError}`,
        { variant: editSuccess ? 'success' : 'error' }
      );
      if (editSuccess) {
        history.goBack();
      }
    }
  }, [editSuccess, editError, enqueueSnackbar, id, intl, history]);

  const localLocation = useMemo(() => {
    const localLocation = localStorage.getItem('location');
    const parseLocalLocation = localLocation && JSON.parse(localLocation);
    return parseLocalLocation;
  }, []);

  const formik = useFormikOrderPage({ order, id });

  const { values, setValues, handleSubmit } = formik;

  useEffect(() => {
    fetchCDEK(order?.point?.city_name || chosenCdekCity?.city || 'Москва');
  }, [chosenCdekCity]);

  useEffect(() => {
    if (order) {
      setValues(getInitialValuesOrderPage(order));
    }
  }, [order, setValues]);

  const translates: { [type: string]: string } = {
    cart: intl.formatMessage({ id: 'ORDER.TYPE.CART' }),
    new: intl.formatMessage({ id: 'ORDER.TYPE.NEW' }),
    formation: intl.formatMessage({ id: 'ORDER.TYPE.FORMATION' }),
    ready_to_delivery: intl.formatMessage({ id: 'ORDER.TYPE.DELIVERY' }),
    // payed: intl.formatMessage({ id: 'ORDER.TYPE.PAYED' }),
    canceled: intl.formatMessage({ id: 'ORDER.TYPE.CANCELED' }),
    completed: intl.formatMessage({ id: 'ORDER.TYPE.COMPLETED' }),
    in_processing: intl.formatMessage({ id: 'ORDER.TYPE.PROCCESS' }),
  };
  const orderTypes = getOrderTypeWithTranslates(translates, true, false);
  const mappedOrders = order?.main_order?.orders_id.map(id => ({ id })) || [];

  if (isRoleAdmin) {
    setLayoutSubheader({
      rightTitle:
        mode === ViewMode.VIEW || (order && order.orders && order.orders.length > 0)
          ? intl.formatMessage({ id: 'ORDER.HEADER.VIEW.EDIT' })
          : '',
      title:
        mode === ViewMode.VIEW
          ? `${intl.formatMessage({ id: 'ORDER.HEADER.VIEW' })} № ${
              order?.main_order ? order.main_order.id : order?.id || ''
            }, ${intl.formatMessage({ id: 'ORDER.HEADER.VIEW.TITLE' })}`
          : `${intl.formatMessage({ id: 'ORDER.HEADER.EDIT' })}`,
      onPress() {
        const newMode = mode === ViewMode.VIEW ? ViewMode.EDIT : ViewMode.VIEW;
        history.push(`/orders/${id}/${newMode}`);
      },
      subTitle: order?.main_order ? 'Просмотр подзаказа №' : '',
      orders: order?.orders ? order?.orders : [],
      items: order,
      ordersId: mappedOrders,
    });
  } else {
    setLayoutSubheader({
      title:
        mode === ViewMode.VIEW
          ? `${intl.formatMessage({ id: 'ORDER.HEADER.VIEW' })}`
          : `${intl.formatMessage({ id: 'ORDER.HEADER.EDIT' })}`,
    });
  }

  const submitCallback = useCallback(() => {
    if (
      order &&
      (order.status === 'payed' ||
        order.status === 'completed' ||
        order.status === 'ready_to_delivery')
    ) {
      setAlertOpen(true);
    } else {
      handleSubmit();
    }
  }, [order, handleSubmit]);

  const selectAction = useCallback(
    id => {
      setSelectedId(id);
    },
    [setSelectedId]
  );

  const alertText = `${intl.formatMessage({ id: 'ORDER.ALERT.EDIT.TEXT1' })} "${
    translates[order?.status || '']
  }". ${intl.formatMessage({ id: 'ORDER.ALERT.EDIT.TEXT2' })}`;

  if (loading) {
    return <></>;
  }

  return (
    <>
      <AlertDialog
        open={isAlertOpen}
        message={alertText}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          setAlertOpen(false);
        }}
        handleAgree={() => handleSubmit()}
      />
      <Card className={homeClasses.container}>
        <Tabs
          value={valueTabs}
          onChange={handleTabsChange}
          variant='scrollable'
          indicatorColor='primary'
          textColor='primary'
          aria-label='tabs'
        >
          <Tab label={intl.formatMessage({ id: 'ORDER.VIEW.TABS.FIELDS' })} />
          <Tab label={intl.formatMessage({ id: 'ORDER.VIEW.TABS.ITEMS' })} />
        </Tabs>
        <div className={homeClasses.table}>
          <TabPanel value={valueTabs} index={0}>
            {loading ? (
              Array.from(Array(5)).map((_, idx) => (
                <Skeleton
                  key={idx}
                  width='100%'
                  height={86}
                  animation='wave'
                  style={{ marginTop: -5, marginBottom: -5 }}
                />
              ))
            ) : (
              <OrderForm
                formik={formik}
                disabled={disabled}
                submitCallback={submitCallback}
                chosenCdekCity={chosenCdekCity}
                setChosenCdekCity={setChosenCdekCity}
                cdekCitiesToShow={cdekCitiesToShow}
                setShippingLocation={setShippingLocation}
                orderTypes={orderTypes}
                shippingLocation={shippingLocation}
                stores={stores}
              />
            )}
          </TabPanel>
          <TabPanel value={valueTabs} index={1}>
            <OrderTable
              orders={order || values}
              selectAction={selectAction}
              selectedId={selectedId}
              editStatus={editStatus}
            />
          </TabPanel>
        </div>
      </Card>
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    order: state.orders.order,
    loading: state.orders.byIdLoading,
    editError: state.orders.editError,
    editSuccess: state.orders.editSuccess,
    cdekCities: state.yaMap.cdekCities,
  }),
  {
    fetchById: orderActions.fetchByIdRequest,
    clear: orderActions.clearEdit,
    editStatus: orderActions.editStatusRequest,

    fetchCDEK: mapActions.fetchRequestCdek,
    fetchCDEKCities: mapActions.fetchRequestCdekCities,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(OrderPage);
